import { addDays, DateOnly } from '@if/core/date'
import { EApplicantType, EDocumentType, ERequiredFrom } from '@if/core/types'
import { RequiredExternalStep } from '@src/types/CreditApplicationSchema'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export type IdentityVerificationByApplicant = Partial<Record<EApplicantType, RequiredExternalStep | null>>

/**
 * A photo ID locked to the credit agent is only actionable by the merchant when the applicant also
 * has an identity verification step: the ID then arrives through Vouched rather than an upload.
 * Any other credit-agent-locked document stays out of the merchant's hands entirely.
 *
 * Gated on EnableVouchedForThinCreditB2B so that turning the switch off restores the previous
 * behaviour for applications that already have a credit-agent-locked photo ID: the row is hidden
 * again rather than falling back to an upload field.
 */
export function isIdentityVerificationDocument(
  documentRequired: RequiredDocument,
  identityVerification: RequiredExternalStep | null | undefined,
  isThinFileVouchedEnabled: boolean,
): boolean {
  return (
    isThinFileVouchedEnabled &&
    documentRequired.requiredFrom === ERequiredFrom.CreditAgent &&
    documentRequired.typeId === EDocumentType.ValidPhotoId &&
    !!identityVerification
  )
}

export function useFullNameText(documentRequired: RequiredDocument) {
  const { t } = useTranslation()

  const fullNameText = useMemo(() => {
    let applicantSuffix = ''
    if (documentRequired.applicantType === EApplicantType.Applicant) {
      applicantSuffix = ` - ${t('common.applicant')}`
    } else if (documentRequired.applicantType === EApplicantType.CoApplicant) {
      applicantSuffix = ` - ${t('common.coApplicant')}`
    }

    return t('common.currentLanguage') === 'Fr'
      ? `${documentRequired.nameFr}${applicantSuffix}`
      : `${documentRequired.nameEn}${applicantSuffix}`
  }, [documentRequired, t])

  return fullNameText
}

export function canSendOrDeleteHighEstimateInvoice(firstPaymentOn: DateOnly) {
  const maxDateToSendInvoice = new DateOnly(addDays(firstPaymentOn, -5))
  return DateOnly.today() <= maxDateToSendInvoice
}
